import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { logErrorEvent } from "@/lib/logError";
import {
  isStaleBundleError,
  recoverFromStaleBundle,
} from "@/lib/staleBundleRecovery";

/**
 * Per-route error boundary. Sits inside the global ErrorBoundary so a
 * single broken page (e.g. Menus throws) doesn't blank the whole app —
 * instead the user sees a scoped fallback with a way back to safety
 * (Home / My recipes) while the rest of the chrome stays functional.
 *
 * Stale-bundle errors fall through to the same recovery path as the
 * global boundary so a cached chunk on a specific lazy route still
 * triggers the same reload-with-clean-cache flow.
 */
type Props = { children: ReactNode; routeLabel: string };
type State = { hasError: boolean; recovering: boolean };

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, recovering: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, recovering: isStaleBundleError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`Route "${this.props.routeLabel}" error:`, error, info);
    logErrorEvent({
      code: error.name || "route_render_error",
      source: `route:${this.props.routeLabel}`,
      message: error.message,
      stack: info?.componentStack || error.stack || null,
    });
    if (isStaleBundleError(error)) {
      const started = recoverFromStaleBundle();
      if (!started) this.setState({ recovering: false });
    }
  }

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.state.recovering) {
      return (
        <main className="flex min-h-[60vh] items-center justify-center px-6">
          <p className="text-sm text-muted-foreground">Updating to the latest version…</p>
        </main>
      );
    }
    return (
      <main
        id="main"
        className="mx-auto flex max-w-md flex-col items-center justify-center gap-4 px-6 py-24 text-center"
      >
        <h1 className="font-display text-2xl font-semibold text-foreground">
          This page hit a snag
        </h1>
        <p className="text-sm text-muted-foreground">
          We logged the error. Reload the page, or jump back to a safe spot —
          your saved recipes are fine.
        </p>
        <div className="mt-2 flex flex-col items-center gap-2 sm:flex-row">
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex h-11 items-center justify-center rounded-full bg-primary px-5 text-sm font-medium text-primary-foreground shadow-soft transition-smooth hover:opacity-90"
          >
            Reload page
          </button>
          <Link
            to="/my-recipes"
            className="inline-flex h-11 items-center justify-center rounded-full border border-border bg-background px-5 text-sm font-medium text-foreground transition-smooth hover:bg-muted"
          >
            My recipes
          </Link>
          <Link
            to="/"
            className="inline-flex h-11 items-center justify-center rounded-full border border-border bg-background px-5 text-sm font-medium text-foreground transition-smooth hover:bg-muted"
          >
            Home
          </Link>
        </div>
      </main>
    );
  }
}
